import {
  Team,
  Player,
  MatchState,
  TeamState,
  LostReason,
  LOST_REASON_REQUIRES_WINNER,
  MIN_ROTATION_GAP,
  MATCH_CONFIG,
  MatchType,
  ValidationError,
  MatchRulesConfig,
  WIN_DIFFERENCE,
  VolleyballCategory,
  CategoryAssignment,
} from '@/types';
import { v4 as uuidv4 } from 'uuid';

// V-01: Match Number Required
export function validateMatchNumberRequired(matchNumber: number | null | undefined): ValidationError | null {
  if (matchNumber === null || matchNumber === undefined || matchNumber === 0) {
    return {
      id: uuidv4(),
      title: 'Match Number Required',
      message: 'Match number is required.',
    };
  }
  return null;
}

// V-02: Match Number Must Be Positive Integer
export function validateMatchNumberFormat(matchNumber: number): ValidationError | null {
  if (!Number.isInteger(matchNumber) || matchNumber <= 0) {
    return {
      id: uuidv4(),
      title: 'Invalid Match Number',
      message: 'Match number must be a positive number.',
    };
  }
  return null;
}

// V-03: Date Required
export function validateDateRequired(date: string | null | undefined): ValidationError | null {
  if (!date) {
    return {
      id: uuidv4(),
      title: 'Date Required',
      message: 'Match date is required.',
    };
  }
  return null;
}

// V-04 to V-05: Team Required
export function validateTeamRequired(team: Team | null, teamLabel: string): ValidationError | null {
  if (!team) {
    return {
      id: uuidv4(),
      title: 'Team Required',
      message: `Select ${teamLabel}.`,
    };
  }
  return null;
}

// V-06: Match Type Required
export function validateMatchTypeRequired(matchType: MatchType | null): ValidationError | null {
  if (!matchType) {
    return {
      id: uuidv4(),
      title: 'Match Type Required',
      message: 'Select a match type.',
    };
  }
  return null;
}

// V-07: Toss Winner Required
export function validateTossWinnerRequired(tossWonById: string | null): ValidationError | null {
  if (!tossWonById) {
    return {
      id: uuidv4(),
      title: 'Toss Winner Required',
      message: 'Select who won the toss.',
    };
  }
  return null;
}

// V-08: Toss Choice Required
export function validateTossChoiceRequired(tossChoice: 'serve' | 'side' | null): ValidationError | null {
  if (!tossChoice) {
    return {
      id: uuidv4(),
      title: 'Toss Choice Required',
      message: 'Select Serve or Side.',
    };
  }
  return null;
}

// V-09: Same Team Cannot Play Both Sides
export function validateTeamsDifferent(teamAId: string | null, teamBId: string | null): ValidationError | null {
  if (teamAId && teamBId && teamAId === teamBId) {
    return {
      id: uuidv4(),
      title: 'Duplicate Team Selection',
      message: 'Team B must be different from Team A.',
    };
  }
  return null;
}

// V-10: Toss Winner Must Be One of the Playing Teams
export function validateTossWinnerInMatch(
  tossWonById: string | null,
  teamAId: string | null,
  teamBId: string | null
): ValidationError | null {
  if (tossWonById && teamAId && teamBId) {
    if (tossWonById !== teamAId && tossWonById !== teamBId) {
      return {
        id: uuidv4(),
        title: 'Invalid Toss Winner',
        message: 'Toss winner must be one of the playing teams.',
      };
    }
  }
  return null;
}

// V-11: First Server Must Be From Serving Team
export function validateFirstServerTeam(
  player: Player | null,
  servingTeamId: string,
  servingTeamName: string
): ValidationError | null {
  if (!player) {
    return {
      id: uuidv4(),
      title: 'Invalid First Server',
      message: 'Please select a player.',
    };
  }

  if (player.teamId !== servingTeamId) {
    return {
      id: uuidv4(),
      title: 'INVALID FIRST SERVER',
      message: `Only ${servingTeamName} players can serve first. Please select a player from ${servingTeamName}.`,
    };
  }
  return null;
}

// V-30: Server Must Belong To Serving Team (for any server selection)
export function validateServerTeam(
  player: Player,
  servingTeamId: string,
  servingTeamName: string
): ValidationError | null {
  if (player.teamId !== servingTeamId) {
    return {
      id: uuidv4(),
      title: 'INVALID SERVER',
      message: `Only ${servingTeamName} players can serve. Selected player is not from the serving team.`,
    };
  }
  return null;
}

// V-12: Server Too Recently Served (Rotation Violation)
export function validateRotationGap(
  player: Player,
  teamState: TeamState,
  teamName: string,
  rotationCooloff?: number
): ValidationError | null {
  const serveHistory = teamState.rotationState.serveHistory;

  // Find the player's most recent service stint
  const lastServedIndex = serveHistory.lastIndexOf(player.id);
  if (lastServedIndex === -1) {
    return null;
  }

  // Count how many different players served after the player's last stint
  const playersAfterLastStint = new Set(serveHistory.slice(lastServedIndex + 1));

  // Use provided cooloff or fall back to default (MIN_ROTATION_GAP - 1 = 5)
  const requiredOthers = rotationCooloff !== undefined ? rotationCooloff : MIN_ROTATION_GAP - 1;

  if (playersAfterLastStint.size < requiredOthers) {
    const remaining = requiredOthers - playersAfterLastStint.size;
    const plural = remaining === 1 ? 'player' : 'players';
    return {
      id: uuidv4(),
      title: 'ROTATION VIOLATION',
      message: `${player.name} has served too recently. At least ${requiredOthers} other ${teamName} players must serve before ${player.name} can serve again. ${remaining} more ${plural} needed.`,
    };
  }
  return null;
}

// V-13: Check if rotation deadlock has occurred
export function checkRotationDeadlock(teamState: TeamState, rotationCooloff?: number): boolean {
  const requiredOthers = rotationCooloff !== undefined ? rotationCooloff : MIN_ROTATION_GAP - 1;
  const eligiblePlayers = teamState.players.filter(p => {
    const lastServedIndex = teamState.rotationState.serveHistory.lastIndexOf(p.id);
    if (lastServedIndex === -1) return true;

    const playersAfterLastStint = new Set(teamState.rotationState.serveHistory.slice(lastServedIndex + 1));
    return playersAfterLastStint.size >= requiredOthers;
  });

  // If no players are eligible, we have a deadlock
  return eligiblePlayers.length === 0;
}

// V-14: No validation - continuous service is allowed
export function isContinuousService(servingTeamId: string, pointWonById: string): boolean {
  return servingTeamId === pointWonById;
}

// V-16, V-17: Point Winner Required for IN/TOUCH
export function validatePointWinnerRequired(lostReason: LostReason | null, pointWinnerId: string | null): ValidationError | null {
  if (lostReason && LOST_REASON_REQUIRES_WINNER[lostReason] && !pointWinnerId) {
    return {
      id: uuidv4(),
      title: 'PLAYER REQUIRED',
      message: `When the reason is ${lostReason}, the player who caught/touched the ball must be selected.`,
    };
  }
  return null;
}

// V-18 to V-21: Point Winner Forbidden for ACE/OUT/NET/FOUL
export function validatePointWinnerForbidden(lostReason: LostReason | null, pointWinnerId: string | null): ValidationError | null {
  if (lostReason && !LOST_REASON_REQUIRES_WINNER[lostReason] && pointWinnerId) {
    return {
      id: uuidv4(),
      title: 'Point Winner Not Allowed',
      message: `Point winner cannot be recorded for ${lostReason}.`,
    };
  }
  return null;
}

// V-22: Point Winner Must Be From the Rally-Winning Team
export function validatePointWinnerTeam(
  pointWinnerId: string | null,
  winningTeamId: string,
  winningTeamName: string,
  losingTeamName: string,
  teams: { id: string; players: Player[] }[]
): ValidationError | null {
  if (!pointWinnerId) return null;

  const pointWinnerPlayer = teams
    .flatMap(t => t.players)
    .find(p => p.id === pointWinnerId);

  if (pointWinnerPlayer && pointWinnerPlayer.teamId !== winningTeamId) {
    return {
      id: uuidv4(),
      title: 'WRONG TEAM',
      message: `The point winner must be a player from ${winningTeamName}, not ${losingTeamName}.`,
    };
  }
  return null;
}

// V-23: Normal Set Win Condition (now uses dynamic rules)
export function checkSetWin(
  scoreA: number,
  scoreB: number,
  rules?: MatchRulesConfig | null
): { winner: 'A' | 'B' | null; goldenPoint: boolean } {
  const winningPoint = rules?.winningPoint ?? 15;
  const goldenPoint = rules?.goldenPoint ?? 21;

  const maxScore = Math.max(scoreA, scoreB);
  const diff = Math.abs(scoreA - scoreB);

  // Check for golden point trigger
  const goldenPointTrigger = goldenPoint - 1;
  if (scoreA === goldenPointTrigger && scoreB === goldenPointTrigger) {
    return { winner: null, goldenPoint: true };
  }

  // V-26: Golden Point Win
  if (scoreA === goldenPoint) {
    return { winner: 'A', goldenPoint: false };
  }
  if (scoreB === goldenPoint) {
    return { winner: 'B', goldenPoint: false };
  }

  // Check normal win conditions
  if (maxScore >= winningPoint && diff >= WIN_DIFFERENCE) {
    return { winner: scoreA > scoreB ? 'A' : 'B', goldenPoint: false };
  }

  // V-24: Deuce - Continue Play
  return { winner: null, goldenPoint: false };
}

// V-25: Check if golden point mode (now uses dynamic rules)
export function isGoldenPointMode(scoreA: number, scoreB: number, rules?: MatchRulesConfig | null): boolean {
  const goldenPoint = rules?.goldenPoint ?? 21;
  const goldenPointTrigger = goldenPoint - 1;
  return scoreA === goldenPointTrigger && scoreB === goldenPointTrigger;
}

// V-28, V-29: Check if scoring is locked
export function isScoringLocked(matchState: MatchState): { locked: boolean; reason: string } {
  if (matchState.status === 'match-won') {
    return { locked: true, reason: 'Match is complete.' };
  }
  if (matchState.status === 'set-won' && matchState.currentSet) {
    return { locked: true, reason: 'Set is complete. Start next set to continue.' };
  }
  return { locked: false, reason: '' };
}

// Match Win Condition
export function checkMatchWin(setsWonA: number, setsWonB: number, matchType: MatchType): 'A' | 'B' | null {
  const config = MATCH_CONFIG[matchType];
  if (setsWonA >= config.winsNeeded) return 'A';
  if (setsWonB >= config.winsNeeded) return 'B';
  return null;
}

// Get eligible servers for a team
export function getEligibleServers(teamState: TeamState, rotationCooloff?: number): { eligible: Player[]; ineligible: { player: Player; remaining: number }[] } {
  const serveHistory = teamState.rotationState.serveHistory;
  const requiredOthers = rotationCooloff !== undefined ? rotationCooloff : MIN_ROTATION_GAP - 1;

  const eligible: Player[] = [];
  const ineligible: { player: Player; remaining: number }[] = [];

  for (const player of teamState.players) {
    const lastServedIndex = serveHistory.lastIndexOf(player.id);
    if (lastServedIndex === -1) {
      eligible.push(player);
    } else {
      const playersAfterLastStint = new Set(serveHistory.slice(lastServedIndex + 1));
      if (playersAfterLastStint.size >= requiredOthers) {
        eligible.push(player);
      } else {
        const remaining = requiredOthers - playersAfterLastStint.size;
        ineligible.push({
          player,
          remaining,
        });
      }
    }
  }

  return { eligible, ineligible };
}

// Calculate derived first serving team from toss
export function getFirstServingTeam(
  tossWonById: string | null,
  tossChoice: 'serve' | 'side' | null,
  teamAId: string,
  teamBId: string
): string {
  if (!tossWonById || !tossChoice) return teamAId;
  if (tossChoice === 'serve') {
    return tossWonById;
  } else {
    return tossWonById === teamAId ? teamBId : teamAId;
  }
}

// Validate entire match setup
export function validateMatchSetup(data: {
  matchNumber: number | null;
  date: string | null;
  matchType: MatchType | null;
  teamAId: string | null;
  teamBId: string | null;
  tossWonById: string | null;
  tossChoice: 'serve' | 'side' | null;
}): ValidationError[] {
  const errors: ValidationError[] = [];

  const e1 = validateMatchNumberRequired(data.matchNumber);
  if (e1) errors.push(e1);

  if (data.matchNumber) {
    const e2 = validateMatchNumberFormat(data.matchNumber);
    if (e2) errors.push(e2);
  }

  const e3 = validateDateRequired(data.date);
  if (e3) errors.push(e3);

  const e4 = validateTeamRequired(null, 'Team A');
  if (!data.teamAId && e4) errors.push(e4);

  const e5 = validateTeamRequired(null, 'Team B');
  if (!data.teamBId && e5) errors.push(e5);

  const e6 = validateMatchTypeRequired(data.matchType);
  if (e6) errors.push(e6);

  const e7 = validateTossWinnerRequired(data.tossWonById);
  if (e7) errors.push(e7);

  const e8 = validateTossChoiceRequired(data.tossChoice);
  if (e8) errors.push(e8);

  const e9 = validateTeamsDifferent(data.teamAId, data.teamBId);
  if (e9) errors.push(e9);

  const e10 = validateTossWinnerInMatch(data.tossWonById, data.teamAId, data.teamBId);
  if (e10) errors.push(e10);

  return errors;
}

// ============================================
// VOLLEYBALL CATEGORY VALIDATION RULES
// ============================================

/**
 * Validates all Volleyball category assignments for a rally.
 * Returns null if valid, or ValidationError if any rule is violated.
 */
export function validateVolleyballCategories(
  categories: CategoryAssignment[],
  winningTeamId: string,
  _losingTeamId: string,
  servingTeamId: string,
  currentServerId: string | null,
  winningTeamPlayers: Player[],
  losingTeamPlayers: Player[]
): ValidationError | null {
  const byCategory = new Map<VolleyballCategory, CategoryAssignment[]>();
  for (const c of categories) {
    const list = byCategory.get(c.category) || [];
    list.push(c);
    byCategory.set(c.category, list);
  }

  // Rule 1: A only for server
  const acerAssignments = byCategory.get('A') || [];
  if (acerAssignments.length > 0) {
    // Rule 2: A only when serving team wins
    if (winningTeamId !== servingTeamId) {
      return {
        id: uuidv4(),
        title: 'INVALID ACER',
        message: 'Acer (A) can only be awarded when the serving team wins the rally.',
      };
    }
    // Rule 1: A only for current server
    for (const a of acerAssignments) {
      if (a.playerId !== currentServerId) {
        return {
          id: uuidv4(),
          title: 'INVALID ACER',
          message: 'Only the current server can receive an Acer (A).',
        };
      }
    }
    // Maximum one A
    if (acerAssignments.length > 1) {
      return {
        id: uuidv4(),
        title: 'TOO MANY ACERS',
        message: 'Maximum one Acer (A) can be awarded per rally.',
      };
    }
  }

  // Rule 3: Maximum one L per team
  const liberoAssignments = byCategory.get('L') || [];
  if (liberoAssignments.length > 0) {
    // Count L per team (both winning and losing teams are allowed)
    const allPlayers = [...winningTeamPlayers, ...losingTeamPlayers];
    const teamCounts = new Map<string, number>();
    for (const l of liberoAssignments) {
      const player = allPlayers.find(p => p.id === l.playerId);
      if (player) {
        teamCounts.set(player.teamId, (teamCounts.get(player.teamId) || 0) + 1);
      }
    }
    // Check no team has more than 1 L
    for (const [, count] of teamCounts) {
      if (count > 1) {
        return {
          id: uuidv4(),
          title: 'TOO MANY LIBEROS',
          message: 'Maximum one Libero (L) per team per rally.',
        };
      }
    }
    // Maximum 2 L total (one from each team)
    if (liberoAssignments.length > 2) {
      return {
        id: uuidv4(),
        title: 'TOO MANY LIBEROS',
        message: 'Maximum two Liberos (L) can be awarded per rally (one per team).',
      };
    }
  }

  // Rule 4: Maximum one S
  const setterAssignments = byCategory.get('S') || [];
  if (setterAssignments.length > 0) {
    // Rule 5: S only from winning team
    for (const s of setterAssignments) {
      if (!winningTeamPlayers.some(p => p.id === s.playerId)) {
        return {
          id: uuidv4(),
          title: 'INVALID SETTER',
          message: 'Setter (S) can only be awarded to winning team players.',
        };
      }
    }
    if (setterAssignments.length > 1) {
      return {
        id: uuidv4(),
        title: 'TOO MANY SETTERS',
        message: 'Maximum one Setter (S) per rally.',
      };
    }
  }

  // Rule 8: Maximum one B
  const blockerAssignments = byCategory.get('B') || [];
  if (blockerAssignments.length > 0) {
    // Rule 9: B only from winning team
    for (const b of blockerAssignments) {
      if (!winningTeamPlayers.some(p => p.id === b.playerId)) {
        return {
          id: uuidv4(),
          title: 'INVALID BLOCKER',
          message: 'Blocker (B) can only be awarded to winning team players.',
        };
      }
    }
    if (blockerAssignments.length > 1) {
      return {
        id: uuidv4(),
        title: 'TOO MANY BLOCKERS',
        message: 'Maximum one Blocker (B) per rally.',
      };
    }
  }

  // Rule 11: F only from winning team
  const finisherAssignments = byCategory.get('F') || [];
  if (finisherAssignments.length > 0) {
    for (const f of finisherAssignments) {
      if (!winningTeamPlayers.some(p => p.id === f.playerId)) {
        return {
          id: uuidv4(),
          title: 'INVALID FINISHER',
          message: 'Finisher (F) can only be awarded to winning team players.',
        };
      }
    }
    // Rule 12: Maximum one F
    if (finisherAssignments.length > 1) {
      return {
        id: uuidv4(),
        title: 'TOO MANY FINISHERS',
        message: 'Maximum one Finisher (F) per rally.',
      };
    }
  }

  // Rule 6: S requires F (and S player cannot equal F player)
  if (setterAssignments.length > 0) {
    if (finisherAssignments.length === 0) {
      return {
        id: uuidv4(),
        title: 'FINISHER REQUIRED',
        message: 'When Setter (S) is assigned, a Finisher (F) is mandatory.',
      };
    }
    // Rule 7: S player cannot equal F player
    if (setterAssignments[0].playerId === finisherAssignments[0].playerId) {
      return {
        id: uuidv4(),
        title: 'INVALID ASSIGNMENT',
        message: 'The Setter (S) and Finisher (F) must be different players.',
      };
    }
  }

  // Rule 10: B automatically creates F for same player
  if (blockerAssignments.length > 0) {
    const blockerPlayerId = blockerAssignments[0].playerId;
    // If there's a finisher, it must be the same player
    if (finisherAssignments.length > 0 && finisherAssignments[0].playerId !== blockerPlayerId) {
      return {
        id: uuidv4(),
        title: 'INVALID FINISHER',
        message: 'When Blocker (B) is assigned, the Finisher (F) must be the same player.',
      };
    }
  }

  // Rule: S and B are mutually exclusive
  // B auto-assigns F to same player, but S requires different F player
  if (setterAssignments.length > 0 && blockerAssignments.length > 0) {
    return {
      id: uuidv4(),
      title: 'MUTUAL EXCLUSIVITY',
      message: 'Setter (S) and Blocker (B) cannot be assigned together. Blocker auto-assigns Finisher to same player, but Setter requires a different Finisher.',
    };
  }

  // Rule 13-14: D allowed on both teams, unlimited
  // D is always valid - no validation needed beyond type checking

  return null;
}

/**
 * Get eligible categories for a player based on context.
 */
export function getEligibleCategories(
  player: Player,
  isWinningTeam: boolean,
  isServingTeam: boolean,
  isCurrentServer: boolean,
  existingAssignments: CategoryAssignment[],
  winningTeamPlayers: Player[],
  losingTeamPlayers: Player[]
): VolleyballCategory[] {
  const categories: VolleyballCategory[] = [];

  // D - always available for any player (Rule 13-14)
  categories.push('D');

  // A - only for current server when serving team wins (Rules 1-2)
  if (isCurrentServer && isServingTeam && isWinningTeam) {
    categories.push('A');
  }

  // L - available for both teams, max one per team
  const allPlayers = [...winningTeamPlayers, ...losingTeamPlayers];
  const teamLCount = existingAssignments.filter(
    c => c.category === 'L' && allPlayers.some(p => p.id === c.playerId && p.teamId === player.teamId)
  ).length;
  if (teamLCount === 0) {
    categories.push('L');
  }

  if (isWinningTeam) {
    // S - winning team only, max one (Rules 4-5)
    // S not available if B is already assigned (mutual exclusivity)
    const existingS = existingAssignments.filter(c => c.category === 'S').length;
    const existingB = existingAssignments.filter(c => c.category === 'B').length;
    if (existingS === 0 && existingB === 0) {
      categories.push('S');
    }

    // B - winning team only, max one (Rules 8-9)
    // B not available if S is already assigned (mutual exclusivity)
    if (existingB === 0 && existingS === 0) {
      categories.push('B');
    }

    // F - winning team only, max one (Rule 11-12)
    const existingF = existingAssignments.filter(c => c.category === 'F').length;
    if (existingF === 0) {
      // If S exists, F is mandatory and will be forced
      // If B exists, F is auto-assigned to same player, so don't show for others
      const existingBAssignment = existingAssignments.find(c => c.category === 'B');
      if (!existingBAssignment || existingBAssignment.playerId === player.id) {
        categories.push('F');
      }
    }
  }

  return categories;
}

/**
 * Auto-generate mandatory categories after user selection.
 * Returns the final list of categories that should be saved.
 */
export function resolveCategoryDependencies(
  categories: CategoryAssignment[]
): CategoryAssignment[] {
  const result = [...categories];
  const byCategory = new Map<VolleyballCategory, CategoryAssignment[]>();
  for (const c of categories) {
    const list = byCategory.get(c.category) || [];
    list.push(c);
    byCategory.set(c.category, list);
  }

  // Rule 10: If B exists, auto-create F for same player
  const blockerAssignments = byCategory.get('B');
  if (blockerAssignments && blockerAssignments.length > 0) {
    const blockerPlayerId = blockerAssignments[0].playerId;
    const existingF = result.find(c => c.category === 'F');
    if (!existingF) {
      // Auto-add F for the blocker
      result.push({ playerId: blockerPlayerId, category: 'F' });
    }
  }

  return result;
}
